import { useTranslation } from 'react-i18next';
import { AddSellerContactPanel } from '~/components/common/AddSellerContactPanel';
import { ContactCheckboxList } from '~/components/common/ContactCheckboxList';
import { SellerAccordionRow } from '~/components/common/SellerAccordionRow';
import { statusBadgeClasses } from '~/components/ui/statusBadgeClasses';
import { sellerLabel } from '~/utils';
import type { OrganizationContactData } from '~/api';
import type { RecipientOrganization } from '~/utils/contact';
import { toggleId } from '~/utils/toggleId';

export type SellerContactOrganization = RecipientOrganization & {
  hasPendingNegotiation?: boolean;
};

export function OrgContactRow({
  organization,
  expanded,
  onToggleExpand,
  selectedContactIds,
  onSelectionChange,
  organizationContactData,
  loaded,
  noContactsSelectedLabel,
  noContactsLabel,
  showAddContact = false,
  addContactDisabled = false,
}: Readonly<{
  organization: SellerContactOrganization;
  expanded: boolean;
  onToggleExpand: () => void;
  selectedContactIds: string[];
  onSelectionChange: (organizationId: string, contactIds: string[]) => void;
  organizationContactData: OrganizationContactData | undefined;
  loaded: boolean;
  noContactsSelectedLabel: string;
  noContactsLabel: string;
  showAddContact?: boolean;
  addContactDisabled?: boolean;
}>) {
  const { t } = useTranslation('order-builder');
  const label = sellerLabel(organization);
  const contacts = organizationContactData
    ? organizationContactData.sellerContacts
    : [];
  const hasContacts = contacts.length > 0;
  const selectedIds = new Set(selectedContactIds);

  const toggleContact = (contactId: string) => {
    onSelectionChange(
      organization.id,
      Array.from(toggleId(selectedIds, contactId))
    );
  };

  const selectAddedContact = (contactId: string) => {
    onSelectionChange(organization.id, [...selectedContactIds, contactId]);
    if (!expanded) {
      onToggleExpand();
    }
  };

  if (organization.hasPendingNegotiation) {
    return (
      <li className="border-b border-neut-border last:border-b-0 px-1 py-3 flex flex-wrap items-center gap-2">
        <span className="text-sm">{label}</span>
        <span className={`${statusBadgeClasses('pending')} whitespace-nowrap`}>
          {t('order_builder.seller_contacts.negotiation_outstanding')}
        </span>
      </li>
    );
  }

  return (
    <AddSellerContactPanel
      organizationId={organization.id}
      testId={`request-quote-add-contact-${organization.id}`}
      disabled={addContactDisabled}
      onAdded={(contact) => selectAddedContact(contact.id)}
    >
      {({ trigger, form }) => (
        <SellerAccordionRow
          disabled={false}
          canExpand={loaded && hasContacts}
          expanded={expanded}
          onToggleExpand={onToggleExpand}
          containerClassName="border-b border-neut-border last:border-b-0"
          headerClassName="flex items-center gap-2 px-1 py-3"
          trailingControl={showAddContact ? trigger : null}
          belowHeader={form && <div className="px-1 pb-3">{form}</div>}
          expandedContent={
            <ul className="pl-8 pb-2 space-y-1">
              <ContactCheckboxList
                contacts={contacts}
                selectedContactIds={selectedIds}
                onToggle={(contact) => toggleContact(contact.id)}
                checkboxIdPrefix={`select-contact-${organization.id}`}
                itemClassName="flex items-center gap-2 py-1"
              />
            </ul>
          }
        >
          <span className="text-sm w-full">
            {label}
            {selectedIds.size > 0 && (
              <span className="text-xs text-neut-sec ml-2">
                {t('order_builder.seller_contacts.selected_count', {
                  count: selectedIds.size,
                })}
              </span>
            )}
            {loaded && hasContacts && selectedIds.size === 0 && (
              <span
                className={`${statusBadgeClasses('warning')} whitespace-nowrap ml-2`}
              >
                {noContactsSelectedLabel}
              </span>
            )}
            {loaded && !hasContacts && (
              <span className="block text-xs text-neut-sec">
                {noContactsLabel}
              </span>
            )}
          </span>
        </SellerAccordionRow>
      )}
    </AddSellerContactPanel>
  );
}
